import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IEventsState } from "./types";
import { IIDAStore } from "../types";
import {
  endOfDay,
  startOfDay,
  min,
  max,
  addYears,
  endOfYear,
  startOfYear,
  isSameDay
} from "date-fns";
import {
  Events,
  IDeleteEventsInPeriod,
  IDeleteEventsOnDates,
  IEventsPost,
  IPostRecurrentEvents,
  IPutRecurrentEvents
} from "../../models/events";
import { EventParticipations } from "../../models/eventParticipations";
import { eventsService } from "../../services/events.service";
import { dutiesService } from "../../services/Duties.services";
import { eCode, EventTypes } from "../../models/eventtypes";
import { EventsPostback, RecurrenceErrorPostback } from "../../models/eventsPostback-models";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { IPeriode } from "../../utils/dateUtils";
import { EventBus } from "@/eventbus";

export const getInitialState = (): IEventsState => {
  return {
    searching: false,
    events: new Map(),
    eventsKeys: new Map(),
    eventParticipations: [],
    eventParticipationsKeys: {},
    eventTypes: [],
    eventsStoreCache: new StoreCache(),
    eventParticipationsStoreCache: new StoreCache()
  };
};

export const employeeIdForEventsWithoutPersons = -999;

const namespaced = true;

export const getters: GetterTree<IEventsState, IIDAStore> = {
  GetEvents: (state: IEventsState) => (from: Date, to: Date) => {
    return Object.values(state.events).filter(
      (events: Events) => events.start_time <= to && events.end_time > from
    );
  },
  GetCloseDayEventypeID: (state: IEventsState) => {
    return (
      state.eventTypes.find((eventtypes: EventTypes) => eventtypes.code === eCode.ClosedEvent)
        ?.id || 0
    );
  },
  GetPupilFreeID: (state: IEventsState) => {
    return (
      state.eventTypes.find(
        (eventtypes: EventTypes) => eventtypes.code === eCode.PupilFreeTimeEvent
      )?.id || 0
    );
  },
  GetEventById: (state: IEventsState) => (eventId: number) => {
    return state.events[eventId];
  },
  GetEventsByEmployeeId: (state: IEventsState) => (employeeId: number) => {
    if (employeeId !== employeeIdForEventsWithoutPersons) {
      const events = <Events[]>Object.values(state.events).filter((event: Events) => {
        return state.eventParticipations.some(
          (ep) => ep.employee_id === employeeId && event.id === ep.event_id
        );
      });
      return events.sort((a: Events, b: Events) => (a.start_time > b.start_time ? 1 : -1));
    } else {
      const events = Object.values(state.events).filter((event: Events) => {
        return !state.eventParticipations.find((ep) => event.id === ep.event_id);
      });

      return events.sort((a: Events, b: Events) => (a.start_time > b.start_time ? 1 : -1));
    }
  },
  GetEventsByDate:
    (state) =>
    (dato: Date): Events[] => {
      const events = state.eventsKeys[startOfDay(dato).getTime()];
      if (events) {
        return Object.values(events);
      } else {
        return [];
      }
    },
  GetEventsByEmployeeIdAndDate: (state, getters) => (employeeId: number, dato: Date) => {
    const events: Events[] = getters.GetEventsByDate(dato);
    const list: Events[] = [];
    if (employeeId !== employeeIdForEventsWithoutPersons) {
      for (let index = 0; index < events.length; index++) {
        const event = events[index];
        if (state.eventParticipationsKeys[event.id.toString()]) {
          if (
            state.eventParticipationsKeys[event.id.toString()].some(
              (ep) => ep.employee_id === employeeId
            )
          ) {
            list.push(event);
          }
        }
      }
    } else {
      for (let index = 0; index < events.length; index++) {
        const event = events[index];
        if (!state.eventParticipationsKeys[event.id.toString()][0]) {
          list.push(event);
        }
      }
    }
    return list;
  }
};

export const actions: ActionTree<IEventsState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  deleteRecurrentEvents({ commit }, eventId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEvents(eventId)
        .then((response) => {
          commit("deleteEvent", eventId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEventsInPeriod({ commit }, event: IDeleteEventsInPeriod): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEventsInPeriod(event)
        .then((response) => {
          commit("deleteEventsInPeriod", event);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEventsOnDates({ commit }, event: IDeleteEventsOnDates): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEventsOnDates(event)
        .then((response) => {
          commit("deleteEventsOnDates", event);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEvents({ commit }, eventId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteEvents(eventId)
        .then((response) => {
          commit("deleteEvent", eventId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createEvents({ commit }, event: IEventsPost): Promise<Events> {
    return new Promise((resolve, reject) => {
      eventsService
        .createEvents(event)
        .then((newEvent: EventsPostback) => {
          commit("updateEvents", { events: [newEvent.event], from: new Date(), to: new Date() });
          commit("updateEventParticipations", {
            eventParticipations: newEvent.event_participations,
            from: new Date(),
            to: new Date()
          });
          commit("duties/updateDuties", { duties: newEvent.duties }, { root: true });
          resolve(newEvent.event);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEvents({ commit }, event: IEventsPost): Promise<Events> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateEvents(event)
        .then((newEvent: EventsPostback) => {
          commit("deleteEventParticipations", newEvent.deleted_event_participations);
          commit("updateEvents", { events: [newEvent.event], from: new Date(), to: new Date() });
          commit("updateEventParticipations", {
            eventParticipations: newEvent.event_participations,
            from: new Date(),
            to: new Date()
          });
          commit("duties/deleteDuties", newEvent.deleted_duties, { root: true });
          commit("duties/updateDuties", { duties: newEvent.duties }, { root: true });
          resolve(newEvent.event);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchEvents({ commit, state }, query: { from: Date; to: Date }): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const periode = { fra: from, tilOgMed: to } as IPeriode;
      if (!state.eventsStoreCache.isCached(periode)) {
        try {
          eventsService.fetchEvents(from, to).then((events) => {
            commit("updateEvents", { events, from, to });
            return resolve(events);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const events: Events[] = Object.values(state.events).filter(
          (ab) => ab.end_time >= from && ab.start_time < to
        );
        return resolve(events);
      }
    });
  },
  fetchEventsByRecurrenceId({ commit, state }, query: { recurrenceId: number }): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      try {
        const events: Events[] = Object.values(state.events).filter(
          (ab: Events) => ab.recurrent_event_id === query.recurrenceId
        );
        if (events.length > 0) {
          events.sort((a: Events, b: Events) => (a.start_time > b.start_time ? 1 : -1));
          return resolve(events);
        } else {
          eventsService.fetchEventsByRecurrenceId(query.recurrenceId).then((events) => {
            return resolve(events);
          });
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  fetchEventParticipations(
    { commit, state },
    query: { employeeIdList: number[]; from: Date; to: Date }
  ): Promise<EventParticipations[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const periode = { fra: from, tilOgMed: to, Objects: [...query.employeeIdList] } as IPeriode;
      if (!state.eventParticipationsStoreCache.isCached(periode)) {
        try {
          eventsService
            .fetchEventParticipations(query.employeeIdList, from, to)
            .then((eventParticipations) => {
              commit("updateEventParticipations", { eventParticipations, from, to });
              return resolve(eventParticipations);
            });
        } catch (error) {
          return reject(error);
        }
      } else {
        const eventParticipations: EventParticipations[] = state.eventParticipations.filter(
          () => true
        );
        return resolve(eventParticipations);
      }
    });
  },
  fetchEventParticipationsByEventId(
    { commit, state },
    query: { eventId: number; forceUpdate: boolean }
  ): Promise<EventParticipations[]> {
    return new Promise((resolve, reject) => {
      try {
        const eventParticipants = state.eventParticipations.filter(
          (ep) => ep.event_id === query.eventId
        );
        if (eventParticipants.length > 0 && !query.forceUpdate) {
          resolve(eventParticipants);
        } else {
          eventsService
            .fetchEventParticipationsByEventId(query.eventId)
            .then((eventParticipations) => {
              commit("updateEventParticipations", { eventParticipations });
              return resolve(eventParticipations);
            });
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  fetchEventTypes({ commit, state }): Promise<EventTypes[]> {
    return new Promise((resolve, reject) => {
      if (state.eventTypes.length == 0) {
        eventsService
          .fetchEventTypes()
          .then((eventstypes) => {
            commit("updateEventTypes", { eventTypes: eventstypes });
            return resolve(eventstypes);
          })
          .catch((error) => {
            return reject(error);
          });
      } else {
        return resolve(state.eventTypes);
      }
    });
  },

  updateEventType({ commit }, eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateEventTypes(eventType)
        .then((eventstypes) => {
          commit("updateEventTypes", { eventTypes: [eventstypes] });
          return resolve(eventstypes);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
  createEventType({ commit }, eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      eventsService
        .createEventType(eventType)
        .then((eventstypes) => {
          commit("updateEventTypes", { eventTypes: [eventstypes] });
          return resolve(eventstypes);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
  deleteEventType({ commit }, eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteEventType(eventType)
        .then(() => {
          commit("deleteEventType", { eventType: eventType });
          return resolve(eventType);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },

  createRecurrentEvents(
    { commit, state },
    query: { firstEventDate: Date; recurrentInfo: IPostRecurrentEvents }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .createRecurrentEvents(query.recurrentInfo)
        .then((postback: RecurrenceErrorPostback) => {
          resolve(postback);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },

  updateRecurrentEvents({ commit, state }, recurrentInfo: IPutRecurrentEvents): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateRecurrentEvents(recurrentInfo)
        .then((postback: RecurrenceErrorPostback) => {
          const employeeIds: number[] = recurrentInfo.events.employees.map((e) => e.employee_id);
          let fromDate = new Date();
          let toDate = new Date();
          if (recurrentInfo.dates) {
            fromDate = min(recurrentInfo.dates);
            toDate = max(recurrentInfo.dates);
          } else if (recurrentInfo.from_date && recurrentInfo.to_date) {
            fromDate = recurrentInfo.from_date;
            toDate = recurrentInfo.to_date;
          }
          const periode = {
            fra: fromDate,
            tilOgMed: toDate,
            Objects: [...employeeIds]
          } as IPeriode;
          if (!state.eventParticipationsStoreCache.isCached(periode)) {
            try {
              eventsService
                .fetchEventParticipations(employeeIds, fromDate, toDate)
                .then((eventParticipations) => {
                  commit("updateEventParticipations", { eventParticipations, fromDate, toDate });
                });
            } catch (error) {
              return reject(error);
            }
            try {
              const from = startOfYear(addYears(new Date(), -1));
              const to = endOfYear(addYears(new Date(), 1));
              eventsService.fetchEvents(from, to).then((events) => {
                commit("updateEvents", { events, fromDate, toDate });
              });
            } catch (error) {
              return reject(error);
            }
          }
          try {
            dutiesService.fetchDuties(fromDate, toDate).then((duties) => {
              commit("duties/updateDuties", { duties: duties }, { root: true });
            });
          } catch (error) {
            return reject(error);
          }
          resolve(postback);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IEventsState> = {
  reset(state: IEventsState): void {
    Object.assign(state, getInitialState());
  },
  deleteEvent(state: IEventsState, eventId: number): void {
    const oldEvent = state.events[eventId];
    if (oldEvent) {
      delete state.eventsKeys[startOfDay(oldEvent.start_time).getTime()][eventId];
    }
    delete state.events[eventId];
  },
  deleteEventsOnDates(state: IEventsState, payload: IDeleteEventsOnDates): void {
    const eventsToRemoveFromState = Array.from(state.events.values()).filter((event) => {
      return (
        event.recurrent_event_id === payload.recurrent_event_id &&
        payload.dates.some((date) => isSameDay(date, event.start_time))
      );
    });
    eventsToRemoveFromState.forEach((event) => {
      removeFromState(state, event);
    });
  },
  deleteEventsInPeriod(state: IEventsState, payload: IDeleteEventsInPeriod): void {
    const eventsToRemoveFromState = Array.from(state.events.values()).filter((event) => {
      return (
        event.recurrent_event_id === payload.recurrent_event_id &&
        event.start_time >= payload.from_date &&
        event.start_time <= payload.to_date
      );
    });
    eventsToRemoveFromState.forEach((event) => {
      removeFromState(state, event);
    });
  },
  updateEvents(state: IEventsState, payload: { events: Events[]; from: Date; to: Date }): void {
    for (let index = 0; index < payload.events.length; index++) {
      const event: Events = payload.events[index];
      const olddate = state.events[event.id]?.start_time;
      state.events[event.id] = event;

      // event participation keys, bc we need an empty person arrays too
      if (!state.eventParticipationsKeys[event.id]) {
        state.eventParticipationsKeys[event.id] = [];
      }

      // event keys
      if (olddate && olddate.getTime() != event.start_time.getTime()) {
        delete state.eventsKeys[startOfDay(olddate).getTime()][event.id];
      }
      setKey(state.eventsKeys, startOfDay(event.start_time).getTime(), event);
    }

    if (payload.from && payload.to) {
      state.eventsStoreCache.addCache(
        new storeCachePeriode({ fra: payload.from, tilOgMed: payload.to })
      );
    }
  },
  updateEventParticipations(
    state: IEventsState,
    payload: { eventParticipations: EventParticipations[]; from: Date; to: Date }
  ): void {
    state.eventParticipations = [
      ...state.eventParticipations,
      ...payload.eventParticipations.filter(
        (newEvents) => !state.eventParticipations.find((existing) => existing.equals(newEvents))
      )
    ];

    for (let index = 0; index < payload.eventParticipations.length; index++) {
      const eventParticipation: EventParticipations = payload.eventParticipations[index];
      const key = eventParticipation.event_id;
      let dayEvents = state.eventParticipationsKeys[key];
      if (!dayEvents) {
        dayEvents = state.eventParticipationsKeys[key] = [];
      }
      const ix = dayEvents.findIndex((d) => d.equals(eventParticipation));
      if (ix > -1) {
        dayEvents[ix] = eventParticipation;
      } else {
        dayEvents.push(eventParticipation);
      }
    }

    if (payload.from && payload.to) {
      state.eventParticipationsStoreCache.addCache(
        new storeCachePeriode({ fra: payload.from, tilOgMed: payload.to })
      );
    }
  },
  deleteEventParticipations(state: IEventsState, payload: number[]): void {
    state.eventParticipations = state.eventParticipations.filter(
      (stateP) => payload.indexOf(stateP.id) === -1
    );
  },
  updateEventTypes(state: IEventsState, payload: { eventTypes: EventTypes[] }): void {
    state.eventTypes = [
      ...payload.eventTypes,
      ...state.eventTypes.filter(
        (oldEventTypes) => !payload.eventTypes.find((existing) => existing.equals(oldEventTypes))
      )
    ];
  },
  deleteEventType(state: IEventsState, payload: { eventType: EventTypes }): void {
    state.eventTypes = state.eventTypes.filter((existing) => existing.id !== payload.eventType.id);
  }
};

export const eventsStoreModule: Module<IEventsState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

function setKey(statevalue, key, value) {
  const valueFromKey = statevalue[key];
  if (!valueFromKey) {
    statevalue[key] = { [value.id]: value };
  } else {
    valueFromKey[value.id] = value;
  }
}

function removeFromState(state: IEventsState, event) {
  const oldEvent = state.events.get(event.id);
  if (oldEvent) {
    delete state.eventsKeys[startOfDay(oldEvent.start_time).getTime()][event.id];
  }
  state.events.delete(event.id);
}
